import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Spinner } from "@/components/ui/spinner";
import { PROTECTED_ROUTES } from "@/constants/routes";
import { ChatBubble } from "@/features/messaging/components/ChatBubble";
import { MessageComposer } from "@/features/messaging/components/MessageComposer";
import { PinnedProductCard } from "@/features/messaging/components/PinnedProductCard";
import { TypingIndicator } from "@/features/messaging/components/TypingIndicator";
import { useConversation } from "@/features/messaging/hooks/useConversation";
import { useConversationSocket } from "@/features/messaging/hooks/useConversationSocket";
import { useMessages } from "@/features/messaging/hooks/useMessages";
import type { ChatMessage } from "@/features/messaging/types/chat";
import { useAppSelector } from "@/store/hooks";
import { ApiError } from "@/types/api";

function initialsFromName(name: string): string {
  const [first, second] = name.trim().split(/\s+/);
  return `${first?.[0] ?? ""}${second?.[0] ?? ""}`.toUpperCase() || "?";
}

let optimisticMessageCounter = 0;

/** Module-level, not inline in the component: an optimistic bubble's id/
 * timestamp reads the current time, which the component's render body
 * itself never does — only this event-handler-only helper does. */
function createOptimisticMessage(chatId: string, senderId: string, text: string): ChatMessage {
  optimisticMessageCounter += 1;
  return {
    id: `pending-${optimisticMessageCounter}`,
    chatId,
    senderId,
    text,
    attachments: [],
    seenBy: [],
    createdAt: new Date().toISOString(),
    status: "sending",
  };
}

/**
 * docs/ui/routes.md `/chat/:conversationId` — rendered by `ChatPage` as the
 * split-view's right pane (desktop, beside the still-visible list) or the
 * whole screen (mobile, "full-screen push" per the doc). This component
 * doesn't know or care which — it fills whatever `h-full` box its parent
 * gives it, same as `ChatListPage`.
 *
 * Message history (`useMessages`, paginated, newest-page-first) and live
 * messages (`useConversationSocket`, appended as they arrive) are two
 * separate sources merged here — see useMessages.ts's own note on why they
 * aren't combined into one cache. A message sent by *this* tab gets an
 * optimistic "sending" bubble immediately (`pendingMessages`, local state
 * only) so the composer feels instant even before the socket ack returns.
 */
export function ChatConversationPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const chatId = conversationId ?? "";
  const currentUserId = useAppSelector((state) => state.auth.user?.id);

  const conversationQuery = useConversation(chatId);
  const messagesQuery = useMessages(chatId);
  const { liveMessages, isOtherTyping, sendMessage, notifyTyping, markSeen } =
    useConversationSocket(chatId);

  const [pendingMessages, setPendingMessages] = useState<ChatMessage[]>([]);
  const lastSeenIdRef = useRef<string | null>(null);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);

  const historyMessages = useMemo(
    () => [...(messagesQuery.data?.pages ?? [])].flatMap((page) => page.data).reverse(),
    [messagesQuery.data],
  );

  const allMessages = useMemo(() => {
    const byId = new Map<string, ChatMessage>();
    for (const message of [...historyMessages, ...liveMessages]) {
      byId.set(message.id, message);
    }
    const merged = Array.from(byId.values()).sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    const pendingStillWaiting = pendingMessages.filter(
      (pending) => !merged.some((message) => message.text === pending.text && message.senderId === pending.senderId),
    );
    return [...merged, ...pendingStillWaiting];
  }, [historyMessages, liveMessages, pendingMessages]);

  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [allMessages.length]);

  useEffect(() => {
    const lastMessage = allMessages.at(-1);
    if (!lastMessage || lastMessage.senderId === currentUserId) return;
    if (lastSeenIdRef.current === lastMessage.id) return;
    lastSeenIdRef.current = lastMessage.id;
    markSeen(lastMessage.id);
  }, [allMessages, currentUserId, markSeen]);

  function handleSend(text: string) {
    if (!currentUserId) return;
    const optimistic = createOptimisticMessage(chatId, currentUserId, text);
    const tempId = optimistic.id;
    setPendingMessages((prev) => [...prev, optimistic]);

    sendMessage(text)
      .then(() => {
        setPendingMessages((prev) => prev.filter((message) => message.id !== tempId));
      })
      .catch(() => {
        setPendingMessages((prev) =>
          prev.map((message) => (message.id === tempId ? { ...message, status: "failed" } : message)),
        );
      });
  }

  function retryPending(tempId: string) {
    const pending = pendingMessages.find((message) => message.id === tempId);
    if (!pending) return;
    setPendingMessages((prev) => prev.filter((message) => message.id !== tempId));
    handleSend(pending.text);
  }

  if (conversationQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="standalone" />
      </div>
    );
  }

  if (conversationQuery.isError) {
    const isNotFound = conversationQuery.error instanceof ApiError && conversationQuery.error.status === 404;
    return (
      <div className="px-4 py-8">
        <EmptyState
          icon={AlertTriangle}
          heading={isNotFound ? "Conversation not found" : "Couldn't load this conversation"}
          description={
            isNotFound
              ? "This conversation may have been removed, or the link is incorrect."
              : conversationQuery.error instanceof ApiError
                ? conversationQuery.error.message
                : "Something went wrong. Please try again."
          }
          action={
            isNotFound
              ? undefined
              : { label: "Retry", onClick: () => void conversationQuery.refetch() }
          }
        />
      </div>
    );
  }

  const conversation = conversationQuery.data;
  const isProductUnavailable = conversation.product.status !== "active";

  return (
    <div className="flex h-full flex-col px-4 py-4">
      <div className="flex items-center gap-3 border-b border-border pb-3">
        <Button type="button" variant="ghost" size="icon" aria-label="Back to messages" asChild>
          <Link to={PROTECTED_ROUTES.chat}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
        <Avatar size="sm">
          {conversation.otherParty.avatarUrl && (
            <AvatarImage src={conversation.otherParty.avatarUrl} alt="" />
          )}
          <AvatarFallback>{initialsFromName(conversation.otherParty.name)}</AvatarFallback>
        </Avatar>
        <p className="font-medium text-foreground">{conversation.otherParty.name}</p>
      </div>

      <PinnedProductCard product={conversation.product} />

      <div className="flex-1 overflow-y-auto">
        {allMessages.length === 0 && (
          <p className="px-4 py-8 text-center text-body-sm text-muted-foreground">
            Say hello — start the conversation about {conversation.product.title}.
          </p>
        )}
        {allMessages.map((message, index) => (
          <ChatBubble
            key={message.id}
            message={message}
            isOwn={message.senderId === currentUserId}
            isGrouped={allMessages[index - 1]?.senderId === message.senderId}
            onRetry={() => retryPending(message.id)}
          />
        ))}
        <TypingIndicator isVisible={isOtherTyping} />
        <div ref={scrollAnchorRef} />
      </div>

      <MessageComposer
        onSend={handleSend}
        onTyping={notifyTyping}
        isDisabled={isProductUnavailable}
        disabledReason="This listing is no longer available."
      />
    </div>
  );
}
